import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, KeyRound, LogOut } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { MENU } from "./menu-config";
import { useAuthStore } from "@/features/auth/store";
import { logout } from "@/features/auth/api";
import { ChangePasswordModal } from "./ChangePasswordModal";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api/client";

export function Sidebar({ onNavigate }: { onNavigate?: () => void } = {}) {
  const [moMatKhau, setMoMatKhau] = useState(false);
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const nav = useNavigate();
  const loc = useLocation();

  const { data: unreadData } = useQuery({
    queryKey: ["notification", "unread"],
    queryFn: async () => {
      const { data } = await api.get("/notification/unread-count");
      const payload = (data?.data ?? data) as { unread?: number };
      return { unread: payload?.unread ?? 0 };
    },
    refetchInterval: 60_000,
    enabled: !!user,
  });
  const unreadCount = unreadData?.unread ?? 0;

  const initiallyOpen = () => {
    const m: Record<string, boolean> = {};
    for (const g of MENU) {
      if (g.items?.some((s) => loc.pathname.startsWith(s.to))) m[g.label] = true;
    }
    return m;
  };
  const [open, setOpen] = useState<Record<string, boolean>>(initiallyOpen);

  async function onLogout() {
    await logout();
    clear();
    nav("/login", { replace: true });
  }

  const initials = (user?.fullName ?? user?.loginName ?? "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(-2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  return (
    <aside
      className={cn(
        "relative flex flex-col w-[260px] shrink-0",
        "bg-sidebar text-sidebar-foreground",
        "border-r border-sidebar-border",
      )}
    >
      {/* Brand mark */}
      <div className="px-5 pt-5 pb-3 flex items-center gap-3">
        <div className="h-9 w-9 rounded-md brand-chip grid place-items-center font-bold text-sm shadow-inner">
          PK
        </div>
        <div className="font-serif text-xl italic leading-none tracking-tight">
          PKH<span className="not-italic font-sans font-semibold">Office</span>
        </div>
      </div>

      {/* User card */}
      <div className="mx-4 mt-3 mb-4 rounded-lg bg-sidebar-border/40 backdrop-blur-sm p-3 flex items-center gap-3">
        <div className="relative shrink-0">
          <div className="h-10 w-10 rounded-md brand-chip grid place-items-center font-semibold text-sm shadow-inner">
            {initials || "?"}
          </div>
          <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-success ring-2 ring-sidebar" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate">
            {user?.fullName ?? user?.loginName ?? "Người dùng"}
          </div>
          <div className="text-xxs text-white/60 truncate">{user?.orgName ?? "—"}</div>
        </div>
      </div>

      {/* Menu */}
      <nav className="flex-1 overflow-y-auto px-2 pb-3 -mr-1 pr-1">
        {MENU.map((g) => {
          const Icon = g.icon;
          const hasSub = !!g.items?.length;
          const isActive = g.to
            ? loc.pathname === g.to || loc.pathname.startsWith(g.to + "/")
            : (g.items?.some((s) => loc.pathname.startsWith(s.to)) ?? false);
          const isOpen = open[g.label] ?? false;

          if (!hasSub && g.to) {
            const showBadge = g.label === "Thông báo" && unreadCount > 0;
            return (
              <NavLink
                key={g.label}
                to={g.to}
                onClick={onNavigate}
                className={({ isActive: na }) =>
                  cn(
                    "group relative flex items-center gap-3 h-9 px-3 mx-1 rounded-md text-sm transition-colors",
                    na
                      ? "bg-white/5 text-white"
                      : "text-white/70 hover:text-white hover:bg-white/5",
                  )
                }
              >
                {({ isActive: na }) => (
                  <>
                    <span
                      className={cn(
                        "absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r bg-sidebar-accent transition-opacity",
                        na ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                    <span className="flex-1 truncate">{g.label}</span>
                    {showBadge && (
                      <span
                        className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-xxs font-semibold tabular-nums shadow-sm"
                        title={`${unreadCount.toLocaleString("vi-VN")} thông báo chưa đọc`}
                      >
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            );
          }

          return (
            <div key={g.label} className="mx-1">
              <button
                onClick={() => setOpen((s) => ({ ...s, [g.label]: !isOpen }))}
                className={cn(
                  "group w-full flex items-center gap-3 h-9 px-3 rounded-md text-sm transition-colors",
                  isActive
                    ? "bg-white/5 text-white"
                    : "text-white/70 hover:text-white hover:bg-white/5",
                )}
              >
                <span
                  className={cn(
                    "absolute-none h-5 w-[3px] rounded-r transition-opacity",
                    isActive ? "bg-sidebar-accent opacity-100" : "opacity-0",
                  )}
                />
                <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                <span className="flex-1 text-left truncate">{g.label}</span>
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 shrink-0 transition-transform duration-200",
                    isOpen ? "rotate-180" : "",
                  )}
                />
              </button>
              {isOpen && g.items && (
                <ul className="mt-0.5 mb-1.5 pl-9 pr-1 space-y-0.5 animate-fade-in">
                  {g.items.map((s) => (
                    <li key={s.to}>
                      <NavLink
                        to={s.to}
                        onClick={onNavigate}
                        className={({ isActive }) =>
                          cn(
                            "block h-8 leading-8 px-2 rounded-sm text-[13px] truncate transition-colors",
                            isActive
                              ? "text-white bg-white/5 border-l-2 border-sidebar-accent pl-[0.375rem]"
                              : "text-white/60 hover:text-white hover:bg-white/5",
                          )
                        }
                      >
                        {s.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer action */}
      <div className="p-3 border-t border-sidebar-border space-y-1">
        <button
          onClick={() => setMoMatKhau(true)}
          className="w-full flex items-center gap-2 h-9 px-3 rounded-md text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors"
        >
          <KeyRound className="h-4 w-4" strokeWidth={1.75} /> Đổi mật khẩu
        </button>
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-2 h-9 px-3 rounded-md text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors"
        >
          <LogOut className="h-4 w-4" strokeWidth={1.75} /> Đăng xuất
        </button>
      </div>

      <ChangePasswordModal
        open={moMatKhau}
        onClose={() => setMoMatKhau(false)}
        onDone={async () => {
          setMoMatKhau(false);
          // Token cũ VẪN còn hiệu lực sau khi đổi mật khẩu (JWT không trạng
          // thái), nên buộc đăng nhập lại để phiên cũ không chạy tiếp.
          alert("Đã đổi mật khẩu. Vui lòng đăng nhập lại bằng mật khẩu mới.");
          await onLogout();
        }}
      />
    </aside>
  );
}
